/**
 * useMapData — Shared data hook for Market Map.
 * Used by LiveMap component.
 * Pure data fetching, no layout/theme logic.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isRemoteMode } from '../../../config/repository';
import { apiFetch } from '../../../data/ApiModuleRepository';

// ═══════════════════════════════════════════════════════════
// TYPES (re-exported for consumers)
// ═══════════════════════════════════════════════════════════

export interface MapLocation {
  id: string;
  org_id: string;
  brand_id: string;
  branch_id: string;
  name: string;
  type: 'INTERNAL' | 'COMPETITOR';
  lat: number;
  lon: number;
  daily_target: number;
  monthly_target: number;
  competitor_tag: string;
  competitor_tag_id: string;
  description: string;
  address: string;
  icon_color: string;
  created_at: number;
  updated_at: number;
  current_sales?: number;
  entered_by?: string;
  entered_name?: string;
  sales_updated_at?: number;
  brand_name?: string;
  brand_logo_url?: string;
  branch_name?: string;
  branch_code?: string;
  effective_daily_target?: number;
  effective_monthly_target?: number;
  daily_override?: number;
  service_radius?: number;
  weighted_target_factor?: number;
  google_rating?: number;
  google_place_id?: string;
  tag_name?: string;
  tag_logo_url?: string;
  tag_color?: string;
}

export interface MapSettings {
  org_id: string;
  default_center_lat: number;
  default_center_lon: number;
  default_zoom: number;
  show_internal: number;
  show_competitors: number;
  show_performance: number;
  dark_theme: number;
  tv_layout: 'landscape' | 'portrait';
  map_style: 'dark' | 'silver';
  default_service_radius: number;
  target_calc_rule: 'fixed' | 'weighted';
  auto_cycle_enabled: number;
  auto_cycle_interval: number;
  ai_google_places: number;
  ai_predictive_sales: number;
  spotlight_enabled: number;
  spotlight_duration: number;
  show_sales_events: number;
  show_competitor_events: number;
  tv_orientation: 'landscape' | 'portrait';
  war_room_zoom: number;
  spotlight_zoom: number;
}

export interface CompetitorTag {
  id: string;
  org_id: string;
  name: string;
  logo_url: string;
  color: string;
  sort_order: number;
  created_at: number;
  updated_at: number;
}

export interface ScoutedCompetitor {
  name: string;
  lat: number;
  lng: number;
  rating: number;
  userRatingsTotal: number;
  placeId: string;
  vicinity: string;
}

export interface MapLiveFeedEvent {
  id: string;
  locationId: string;
  storeName: string;
  message: string;
  delta: number;
  newTotal: number;
  timestamp: number;
  lat: number;
  lon: number;
}

export const DEFAULT_SETTINGS: MapSettings = {
  org_id: '',
  default_center_lat: 25.2854,
  default_center_lon: 51.5310,
  default_zoom: 11,
  show_internal: 1,
  show_competitors: 1,
  show_performance: 1,
  dark_theme: 1,
  tv_layout: 'landscape',
  map_style: 'dark',
  default_service_radius: 3000,
  target_calc_rule: 'fixed',
  auto_cycle_enabled: 0,
  auto_cycle_interval: 60,
  ai_google_places: 0,
  ai_predictive_sales: 0,
  spotlight_enabled: 1,
  spotlight_duration: 8,
  show_sales_events: 1,
  show_competitor_events: 1,
  tv_orientation: 'landscape',
  war_room_zoom: 12,
  spotlight_zoom: 16,
};

// ═══════════════════════════════════════════════════════════
// PERFORMANCE HELPERS (shared)
// ═══════════════════════════════════════════════════════════

function getDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export function getEffectiveTarget(loc: MapLocation, now: Date = new Date()): number {
  const dailyOverride = Number(loc.daily_override ?? 0);
  if (dailyOverride > 0) return dailyOverride;

  const monthlyTarget = Number(loc.effective_monthly_target ?? loc.monthly_target ?? 0);
  if (monthlyTarget > 0) return Math.round(monthlyTarget / getDaysInMonth(now));

  return Number(loc.daily_target ?? 0);
}

export function getSalesRatio(loc: MapLocation): number {
  const target = getEffectiveTarget(loc);
  if (!target || target <= 0) return 0;
  return (loc.current_sales ?? 0) / target;
}

export function getPerformanceColor(ratio: number): string {
  if (ratio >= 0.8) return '#10b981';
  if (ratio >= 0.4) return '#f59e0b';
  return '#ef4444';
}

export function getPerformanceLabel(ratio: number): string {
  if (ratio >= 1) return 'On Target';
  if (ratio >= 0.8) return 'Near Target';
  if (ratio >= 0.4) return 'Below Target';
  return 'Critical';
}

export function getPulseSpeed(ratio: number): string {
  if (ratio >= 0.8) return '3s';
  if (ratio >= 0.4) return '2s';
  return '1s';
}

export function formatCurrency(n: number): string {
  return n.toLocaleString('en-QA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function geoDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function getMediaUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';
  const token = localStorage.getItem('dm_jwt');
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API_BASE}${url}${q}`;
}

// ═══════════════════════════════════════════════════════════
// MAP TILES
// ═══════════════════════════════════════════════════════════

export const SILVER_TILE = 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png';
export const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>';

function calculateOrgKPIs(branches: MapLocation[]) {
  const totalSales = branches.reduce((sum, branch) => sum + Number(branch.current_sales ?? 0), 0);
  const totalTarget = branches.reduce((sum, branch) => sum + getEffectiveTarget(branch), 0);

  const greenCount = branches.filter((branch) => {
    const dailyTarget = getEffectiveTarget(branch);
    return dailyTarget > 0 && Number(branch.current_sales ?? 0) >= dailyTarget * 0.8;
  }).length;

  const amberCount = branches.filter((branch) => {
    const dailyTarget = getEffectiveTarget(branch);
    const currentSales = Number(branch.current_sales ?? 0);
    return dailyTarget > 0 && currentSales >= dailyTarget * 0.4 && currentSales < dailyTarget * 0.8;
  }).length;

  const redCount = branches.filter((branch) => {
    const dailyTarget = getEffectiveTarget(branch);
    return dailyTarget > 0 && Number(branch.current_sales ?? 0) < dailyTarget * 0.4;
  }).length;

  const champions = branches.filter((branch) => {
    const dailyTarget = getEffectiveTarget(branch);
    return dailyTarget > 0 && Number(branch.current_sales ?? 0) >= dailyTarget;
  });

  return {
    totalSales,
    totalTarget,
    overallRatio: totalTarget > 0 ? totalSales / totalTarget : 0,
    greenCount,
    amberCount,
    redCount,
    champions,
  };
}

// ═══════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════

export function useMapData() {
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [settings, setSettings] = useState<MapSettings>(DEFAULT_SETTINGS);
  const [competitorTags, setCompetitorTags] = useState<CompetitorTag[]>([]);
  const [liveFeed, setLiveFeed] = useState<MapLiveFeedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const consecutiveErrors = useRef(0);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const salesSnapshotRef = useRef<Map<string, number>>(new Map());
  const hasSalesSnapshotRef = useRef(false);

  const syncSalesSnapshot = useCallback((nextLocations: MapLocation[], emitEvents: boolean) => {
    const nextSnapshot = new Map<string, number>();
    const nextEvents: MapLiveFeedEvent[] = [];
    const now = Date.now();

    nextLocations
      .filter((location) => location.type === 'INTERNAL')
      .forEach((location) => {
        const currentSales = Number(location.current_sales ?? 0);
        const previousSales = salesSnapshotRef.current.get(location.id);

        if (emitEvents && hasSalesSnapshotRef.current && previousSales !== undefined && currentSales > previousSales) {
          const delta = currentSales - previousSales;
          const timestamp = Number(location.sales_updated_at ?? now) || now;
          nextEvents.push({
            id: `${location.id}-${timestamp}`,
            locationId: location.id,
            storeName: location.name,
            message: `⚡ ${location.name} sales increased by QAR ${formatCurrency(delta)}.`,
            delta,
            newTotal: currentSales,
            timestamp,
            lat: location.lat,
            lon: location.lon,
          });
        }

        nextSnapshot.set(location.id, currentSales);
      });

    salesSnapshotRef.current = nextSnapshot;
    hasSalesSnapshotRef.current = true;

    if (nextEvents.length > 0) {
      nextEvents.sort((left, right) => right.timestamp - left.timestamp);
      setLiveFeed((previous) => [...nextEvents, ...previous].slice(0, 20));
    }
  }, []);

  const loadData = useCallback(async ({ detectChanges = false }: { detectChanges?: boolean } = {}) => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const [sumRes, setRes, tagsRes] = await Promise.all([
        apiFetch('/api/map/sales/summary'),
        apiFetch('/api/map/settings'),
        apiFetch('/api/map/competitor-tags'),
      ]);
      const nextLocations = (sumRes as any).data ?? [];
      syncSalesSnapshot(nextLocations, detectChanges);
      setLocations(nextLocations);
      setCompetitorTags((tagsRes as any).data ?? []);
      const s = (setRes as any).data;
      if (s) setSettings(s);
      consecutiveErrors.current = 0;
    } catch {
      consecutiveErrors.current++;
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    refreshTimer.current = setInterval(() => {
      if (consecutiveErrors.current < 3) loadData({ detectChanges: true });
    }, 15000);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [loadData]);

  const internal = useMemo(() => locations.filter(l => l.type === 'INTERNAL'), [locations]);
  const competitors = useMemo(() => locations.filter(l => l.type === 'COMPETITOR'), [locations]);
  const kpis = useMemo(() => calculateOrgKPIs(internal), [internal]);
  const { totalSales, totalTarget, overallRatio, greenCount, amberCount, redCount, champions } = kpis;

  return {
    locations, settings, competitorTags, loading, error,
    liveFeed,
    internal, competitors, totalSales, totalTarget,
    overallRatio, greenCount, amberCount, redCount, champions,
    loadData,
  };
}
